<template>
  <div id="uploads-overview">
    <title-panel
      :button-label="t('uploadsOverview.newDelivery')"
      :count="pageOptions.rows"
      :display-button="isUserRole([Role.Admin, Role.Coworker])"
      :on-button-click="handleNewUploadButtonClick"
      :title="t('uploadsOverview.deliveries')"
    ></title-panel>

    <new-upload-modal
      ref="newUploadModalRef"
      :project-id="projectId"
      :project-name="projectTitle"
      :project-number="projectNumber"
    />

    <table-wrapper
      v-model:page-options="pageOptions"
      v-model:filters="filters"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :loading="isLoading"
      :items="uploads"
      :mode="TableMode.LAZY"
      row-clickable
      :table-style="{ 'table-layout': 'fixed' }"
      @row-click="goToFilesOverview"
    >
      <template #cell(contents)="{ data }">
        <div class="upload-content-count">
          <span v-tooltip.top="t('uploadsOverview.new')">
            {{ data?.contents.new }}
          </span>
          |
          <span v-tooltip.top="t('uploadsOverview.updated')">
            {{ data?.contents.updated }}
          </span>
        </div>
      </template>

      <template #cell(fileStatuses)="{ data }">
        <div v-if="data" class="inspector-statuses-list">
          <span
            v-tooltip.top="t('fileStatus.waitingForFormalControl')"
            class="waiting-for-formal-control-count"
            >{{ data.fileStatuses.waitingForFormalControl }}</span
          >
          ||
          <span v-tooltip.top="t('fileStatus.waitingForContentControl')"
            >{{ data.fileStatuses.waitingForContentControl }} </span
          >|
          <span v-tooltip.top="t('fileStatus.accepted')" class="accepted-count"
            >{{ data.fileStatuses.accepted }} </span
          >|
          <span
            v-tooltip.top="t('fileStatus.acceptedWithRemarks')"
            class="accepted-with-remarks-count"
            >{{ data.fileStatuses.acceptedWithRemarks }} </span
          >||
          <span
            v-tooltip.top="t('fileStatus.rejected')"
            class="rejected-count"
            >{{
              data.fileStatuses.rejected + data.fileStatuses.inspectorRejected
            }}</span
          >
        </div>
      </template>

      <template #cell(getUploadConfirmation)="{ data }">
        <template v-if="data && isUploadConfirmationReady(data)">
          <a
            v-tooltip.top="
              t('uploadsOverview.tooltips.downloadUploadConfirmation')
            "
            :href="getUploadConfirmationFileLink(data._id)"
            target="_blank"
          >
            <font-awesome-icon
              class="upload-confirmation-icon"
              :icon="['far', 'rectangle-list']"
            />
          </a>
        </template>
      </template>
    </table-wrapper>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';
  import axios from 'axios';
  import {
    IDocumentsUploadsSummaryDto,
    IDocumentsUploadsSummaryPageOptionsDto,
    IPageDto,
    Order,
    ProjectType,
    Role,
  } from 'types';
  import endpointsList from '../../helpers/endpointsList';
  import NewUploadModal from '../../components/NewUploadModal/NewUploadModal.vue';
  import { useToast } from '../../composables/useToast';
  import { useRoles } from '@/composables/useRoles';
  import {
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '../TableWrapper';
  import { formatDateForDisplay } from '@/helpers';
  import { Route, routeParams } from '@/routes';
  import { TABLE_NAMESPACE as DOCUMENTS_TABLE_NAMESPACE } from '../FilesOverviewTable/constants';
  import { ProjectDetailsTab } from '@/views/ProjectDetails/ProjectDetails.types';

  const { t } = useI18n();
  const router = useRouter();
  const { createErrorToast } = useToast();
  const { isUserRole } = useRoles();
  const TABLE_NAMESPACE = 'sut';
  const {
    isLoading,
    pageOptions,
    filters,
    filterParams,
    namespaceQueryParams,
  } = useTableWrapper<
    IDocumentsUploadsSummaryDto,
    IDocumentsUploadsSummaryPageOptionsDto
  >(
    TableMode.LAZY,
    'uploadDate',
    getUploads,
    Order.DESC,
    true,
    TABLE_NAMESPACE,
  );

  const props = defineProps<{
    projectId: string;
    projectNumber: string;
    projectTitle: string;
  }>();

  const uploads = ref<IDocumentsUploadsSummaryDto[]>([]);

  function isSupplierColumnVisible(): boolean {
    return isUserRole([Role.Admin, Role.DocuManager, Role.Inspector]);
  }

  const tableFields = computed(() =>
    new TableFieldsBuilder<IDocumentsUploadsSummaryDto>({
      labelTranslationKey: 'uploadsOverview',
      sortable: true,
    })
      .addField('referenceNumber', {
        sortable: true,
        label: '#',
        width: '8%',
      })
      .addField('supplierName', {
        sortable: true,
        width: '17%',
        isVisible: isSupplierColumnVisible(),
      })
      .addField('filesCount', {
        sortable: true,
        width: '7%',
        label: t('uploadsOverview.count'),
      })
      .addField('contentTypeName', {
        sortable: true,
        width: '15%',
        label: t('uploadsOverview.contentType'),
      })
      .addField('contents', {
        sortable: false,
        width: '6%',
        label: t('uploadsOverview.content'),
      })
      .addField('fileStatuses', {
        sortable: false,
        width: '19%',
        label: t('uploadsOverview.documentStatus'),
      })
      .addField('uploadDate', {
        sortable: true,
        width: '12%',
        valueGetter: (i) => formatDateForDisplay(new Date(i.uploadDate)),
      })
      .addField('earliestInspectionDueDate', {
        sortable: true,
        width: '12%',
        label: t('uploadsOverview.inspectionDueDate'),
        valueGetter: (i) =>
          formatDateForDisplay(new Date(i.earliestInspectionDueDate)),
      })
      .addField('getUploadConfirmation' as any, {
        sortable: false,
        width: '4%',
        label: '',
      })
      .build(),
  );

  const filterConfiguration = computed(() =>
    new TableFiltersBuilder<
      IDocumentsUploadsSummaryDto,
      IDocumentsUploadsSummaryPageOptionsDto
    >({
      translationKey: 'uploadsOverview',
    })
      .addInputFilter('referenceNumber', {
        width: '57px',
        customLabel: '#',
      })
      .addInputFilter('supplierName', {
        width: '208px',
      })
      .addSelectFilter('contentTypeName', {
        width: '184px',
        translationKey: undefined,
        // TODO: Get list dynamically
        selectOptions: ['AssetDocumentation'],
      })
      .addDateRangeFilter('uploadDate', {
        dateFromKey: 'uploadDateFrom',
        dateToKey: 'uploadDateTo',
        simpleLabel: true,
      })
      .addDateRangeFilter('earliestInspectionDueDate', {
        dateFromKey: 'inspectionDueDateFrom',
        dateToKey: 'inspectionDueDateTo',
        customLabel: t('uploadsOverview.inspectionDueDate'),
      })
      .addCheckboxFilter('fileStatuses', {
        customLabel: t('uploadsOverview.hideProcessed'),
        filterProperty: 'excludeProcessed',
      })
      .build(),
  );

  async function getUploads() {
    try {
      const response = await axios.get<IPageDto<IDocumentsUploadsSummaryDto>>(
        endpointsList.documentsUploads.getPaginatedDocumentsUploadsSummaryForProject(
          {
            projectId: props.projectId as any,
            order: pageOptions.value.sortOrder,
            page: pageOptions.value.page,
            sortBy: pageOptions.value.sortBy,
            take: pageOptions.value.perPage,
            ...filterParams.value,
          },
        ),
      );
      uploads.value = response.data.data;
      pageOptions.value.rows = response.data.meta.itemCount;
    } catch {
      createErrorToast(t('uploadsOverview.errors.couldNotLoadDeliveries'));
    }
  }

  function goToFilesOverview(data: IDocumentsUploadsSummaryDto) {
    const params = routeParams[Route.PROJECT_DETAILS];
    router.push({
      name: Route.PROJECT_DETAILS,
      params: {
        [params.projectId]: props.projectId,
        [params.tab]: ProjectDetailsTab.DOCUMENTS,
      },
      query: namespaceQueryParams(
        {
          referenceNumber: data.referenceNumber,
        },
        DOCUMENTS_TABLE_NAMESPACE,
      ),
    });
  }

  function isUploadConfirmationReady(
    upload: IDocumentsUploadsSummaryDto,
  ): boolean {
    const relevantUpload = uploads.value?.find(
      (u) => u.referenceNumber === upload.referenceNumber,
    );
    return !!relevantUpload?.isUploadConfirmationReady;
  }

  function getUploadConfirmationFileLink(uploadId: string): string {
    const fileDownloadEndpoint =
      endpointsList.uploadHandler.downloadUploadConfirmationFile(
        ProjectType.SUPPLIER_PROJECT,
        uploadId,
      );
    return `${window.location.origin}/api/${fileDownloadEndpoint}`;
  }

  const newUploadModalRef = ref<InstanceType<typeof NewUploadModal> | null>(
    null,
  );
  function handleNewUploadButtonClick(): void {
    newUploadModalRef.value?.show();
  }
</script>

<style scoped lang="scss">
  #uploads-overview {
    padding: 2rem 0;

    div.upload-content-count {
      font-size: 0.75rem;
    }

    div.inspector-statuses-list {
      display: flex;
      column-gap: 0.3rem;
      font-size: 0.75rem;
      .accepted-count {
        color: $uploads-overview-accepted-color;
      }
      .accepted-with-remarks-count {
        color: $uploads-overview-accepted-with-remarks-color;
      }
      .rejected-count {
        color: $uploads-overview-rejected-color;
      }
    }

    .upload-confirmation-icon {
      color: $primary-color;
    }
  }
</style>
